/**
 * Strategy pattern for condition hydration, serialization, and validation.
 */

import type { ConditionNodeData, BusinessHoursDayConfig } from '../nodes/ConditionNode';
import type { ValidationError } from './workflowValidator';
import { safeCastString, safeCastArray } from './typeGuards';

export interface ConditionStrategy {
    hydrate: (config: Record<string, unknown>) => Partial<ConditionNodeData>;
    serialize: (data: ConditionNodeData) => Record<string, unknown>;
    validate: (data: ConditionNodeData, stepId: string, title: string) => ValidationError[];
}

export function countNonEmptyKeywords(keywords: string[] | undefined): number {
    if (!keywords?.length) return 0;
    return keywords.filter((k) => String(k).trim().length > 0).length;
}

function castBusinessDays(value: unknown): BusinessHoursDayConfig[] | undefined {
    if (!Array.isArray(value)) return undefined;
    return value.filter(
        (item): item is BusinessHoursDayConfig =>
            item != null && typeof item === 'object' && 'businessDay' in item
    );
}

export const CONDITION_STRATEGIES: Record<string, ConditionStrategy> = {
    'Condition.Message.KeywordMatch': {
        hydrate: (config) => ({
            keywords: safeCastArray(config.keywords),
            matchType: safeCastString(config.matchMode) ?? 'Contains',
        }),
        serialize: (data) => ({
            keywords: (data.keywords ?? []).filter((k) => String(k).trim().length > 0),
            matchMode: data.matchType ?? 'Contains',
        }),
        validate: (data, stepId, title) => {
            if (countNonEmptyKeywords(data.keywords) === 0) {
                return [{ stepId, stepTitle: title, message: 'Add at least one keyword.' }];
            }
            return [];
        },
    },

    'Condition.Number.NumberMatch': {
        hydrate: (config) => ({
            phoneNumbers: safeCastArray(config.numbers),
        }),
        serialize: (data) => ({
            numbers: data.phoneNumbers ?? [],
        }),
        validate: (data, stepId, title) => {
            if (!data.phoneNumbers?.length) {
                return [{ stepId, stepTitle: title, message: 'Select at least one phone number.' }];
            }
            return [];
        },
    },

    'Condition.Location.Countries': {
        hydrate: (config) => ({
            countries: safeCastArray(config.countries),
        }),
        serialize: (data) => ({
            countries: data.countries ?? [],
        }),
        validate: (data, stepId, title) => {
            if (!data.countries?.length) {
                return [{ stepId, stepTitle: title, message: 'Select at least one country.' }];
            }
            return [];
        },
    },

    'Condition.Time.RunSchedule': {
        hydrate: (config) => {
            const runSchedule = safeCastString(config.schedule) ?? 'BusinessHours';
            const result: Partial<ConditionNodeData> = { runSchedule };
            if (runSchedule === 'SpecificHours' && config.businessDays && config.timeZone) {
                const businessDays = castBusinessDays(config.businessDays);
                if (businessDays?.length) {
                    result.businessHours = {
                        timeZone: safeCastString(config.timeZone),
                        businessDays,
                    };
                }
            }
            return result;
        },
        serialize: (data) => {
            const schedule = data.runSchedule ?? 'BusinessHours';
            const result: Record<string, unknown> = {
                schedule,
            };
            if (schedule === 'SpecificHours' && data.businessHours) {
                result.timeZone = data.businessHours.timeZone;
                result.businessDays = data.businessHours.businessDays;
            }
            return result;
        },
        validate: (data, stepId, title) => {
            if (data.runSchedule === 'SpecificHours') {
                const businessHours = data.businessHours;
                if (!businessHours?.timeZone?.trim() || !businessHours?.businessDays?.length) {
                    return [
                        {
                            stepId,
                            stepTitle: title,
                            message: 'Configure business hours when using Specific Hours.',
                        },
                    ];
                }
            }
            return [];
        },
    },
};

export function getConditionStrategy(conditionCode: string | undefined): ConditionStrategy | undefined {
    return conditionCode ? CONDITION_STRATEGIES[conditionCode] : undefined;
}
